// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useMemo, useState } from 'react';
import { Algorithm } from '../config/config';
import { useConfig } from '../hooks/other/useConfig';
import { IDP_INFO_KEY } from '../utils/constants';
import { IdpContext, IdpInfo } from './IdpContext';

function defaultScope(algorithm: Algorithm): string {
  // TODO: This logic was written before the UI had formal support for specifying scopes.
  //  Scopes should probably be fully determined by the deployment, instead of the UI
  //  opining at all.
  switch (algorithm) {
    case Algorithm.RS256:
      return 'daml_ledger_api offline_access openid';
    case Algorithm.HS256UNSAFE:
      return 'daml_ledger_api';
    default:
      return '';
  }
}

export const IdpProvider = ({ children }: React.PropsWithChildren) => {
  const config = useConfig();
  const [idpInfo, setIdpInfo] = useState<IdpInfo>(undefined);
  const [idpError, setIdpError] = useState<string | undefined>(undefined);
  const [isLoading, setLoading] = useState(true);

  useEffect(() => {
    const scope = config.auth.scope || defaultScope(config.auth.algorithm);
    if (!idpInfo && config.auth.algorithm === Algorithm.RS256) {
      setIdpInfo({
        ...config.auth,
        clientId: config.auth.client_id,
        redirectUri: window.location.origin,
        scope,
      });
    } else if (!idpInfo && config.auth.algorithm === Algorithm.HS256UNSAFE) {
      setIdpInfo({
        ...config.auth,
        scope,
      });
    }
    setLoading(false);
  }, [config, idpInfo]);

  const value = useMemo(
    () => ({
      idpInfo,
      idpError,
      isLoading,
      setIdpError,
      unsetIdpError: () => setIdpError(undefined),
      unsetIdpInfo: () => {
        setLoading(true);
        localStorage.removeItem(IDP_INFO_KEY);
        setIdpInfo({ algorithm: 'idp-not-set' });
        setLoading(false);
      },
    }),
    [idpInfo, idpError, isLoading],
  );

  return <IdpContext.Provider value={value}>{children}</IdpContext.Provider>;
};
